import { z } from 'zod';

import { baseEntitySchema } from './baseContract';
import { Role } from '../enums';

// Base User schema
export const userSchema = baseEntitySchema.extend({
  email: z.string().email().max(255),
  password: z.string().max(255).nullish(),
  firstName: z.string().max(50).nullish(),
  lastName: z.string().max(50).nullish(),
  photo: z.string().optional(),
  verified: z.boolean(),
  firstLogin: z.boolean().optional(),
  role: z.nativeEnum(Role).default(Role.Admin),
  isActiveOperator: z.boolean().optional(),
  contactEmail: z.string().max(320).nullish(),
  phone: z.string().max(15).nullish(),
  operatorSpecialization: z.string().max(255).nullish(),
  operatorDisplayText: z.string().nullish(),
  isPartner: z.boolean().optional(),
  organizations: z
    .array(
      z.object({
        id: z.string().uuid(),
        name: z.string(),
        url: z.string().url().nullable(),
      }),
    )
    .optional(),
});

// Create DTO schema
export const createUserSchema = userSchema
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    verified: true,
    organizations: true,
  })
  .extend({
    verified: z.boolean().optional(),
    role: z.nativeEnum(Role),
  })
  .strict();

export const createUserOutputSchema = userSchema
  .omit({ organizations: true })
  .extend({
    organizations: z.array(z.object({ id: z.string().uuid() })),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    password: z.undefined().optional(),
  });

// Update DTO schema
export const updateUserSchema = userSchema
  .partial()
  .required({
    id: true,
  })
  .omit({
    organizations: true,
    // We don't want to allow changing email of the user
    email: true,
  })
  .extend({
    password: z.string().max(255).nullish(),
    oldPassword: z.string().max(255).nullish(),
  })
  .strict();

export type UserDTO = z.infer<typeof userSchema>;
export type CreateUserDTO = z.infer<typeof createUserSchema>;
export type UpdateUserDTO = z.infer<typeof updateUserSchema>;
export type CreateUserOutputDTO = z.infer<typeof createUserOutputSchema>;
